/**
 * Controlador de quantidade de produtos
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Box, IconButton, Typography, InputBase } from '@mui/material';
import { Add, Remove } from '@mui/icons-material';

interface ControladorQuantidadeProps {
  quantidade: number;
  onIncrement: () => void;
  onDecrement: () => void;
  min?: number;
  max?: number;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  usandoEstoqueLoja?: boolean; // Para controlar cor da borda
  editavel?: boolean; // Permite digitação manual da quantidade
  onChange?: (valor: number) => void; // Callback para entrada manual
}

export const ControladorQuantidade: React.FC<ControladorQuantidadeProps> = ({
  quantidade,
  onIncrement,
  onDecrement,
  min = 0,
  max = Infinity,
  disabled = false,
  size = 'medium',
  usandoEstoqueLoja = false,
  editavel = false,
  onChange,
}) => {

  // Sempre exibe a quantidade atual do carrinho, nunca preenche automaticamente com loja
  const canQuantidade = Number(quantidade);
  const canDecrement = canQuantidade > min;
  const canIncrement = canQuantidade < max;

  // Estado local para o input editável
  const [inputValue, setInputValue] = useState<string>(String(canQuantidade));

  // Sincroniza o input com a quantidade externa (botões +/-)
  useEffect(() => {
    setInputValue(String(canQuantidade));
  }, [canQuantidade]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, ''); // apenas dígitos
    setInputValue(raw);
  }, []);

  const handleInputBlur = useCallback(() => {
    const parsed = parseInt(inputValue, 10);
    if (isNaN(parsed) || parsed < min) {
      setInputValue(String(min));
      onChange?.(min);
    } else if (parsed > max) {
      setInputValue(String(max));
      onChange?.(max);
    } else {
      setInputValue(String(parsed));
      onChange?.(parsed);
    }
  }, [inputValue, min, max, onChange]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    }
  }, []);

  // Define cor da borda baseado no tipo de estoque
  // Vermelho: usando estoque loja (usandoEstoqueLoja = true e quantidade > 0)
  // Verde: usando estoque normal (usandoEstoqueLoja = false e quantidade > 0)
  // Padrão: sem cor especial (quantidade = 0)
  const getBorderColor = () => {
    if (quantidade > 0) {
      return usandoEstoqueLoja ? '#f44336' : '#4caf50'; // vermelho : verde
    }
    return 'divider'; // cor padrão quando quantidade = 0
  };

  const getSize = () => {
    switch (size) {
      case 'small':
        return { button: 'small' as const, text: 'body2' as const };
      case 'large':
        return { button: 'medium' as const, text: 'h6' as const };
      default:
        return { button: 'small' as const, text: 'body1' as const };
    }
  };

  const sizes = getSize();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        bgcolor: 'background.paper',
        borderRadius: 2,
        border: 2,
        borderColor: getBorderColor(),
        padding: '5px 0 5px 0',
        width: 120,
      }}
    >
      <IconButton
        size={sizes.button}
        onClick={onDecrement}
        disabled={disabled || !canDecrement}
        color="primary"
      >
        <Remove fontSize={size} />
      </IconButton>

      {editavel ? (
        <InputBase
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          inputProps={{
            inputMode: 'numeric',
            style: {
              textAlign: 'center',
              fontWeight: 600,
              fontSize: sizes.text === 'body2' ? '0.875rem' : sizes.text === 'h6' ? '1.25rem' : '1rem',
              padding: 0,
              width: 40,
            },
          }}
          sx={{ minWidth: 40 }}
        />
      ) : (
        <Typography
          variant={sizes.text}
          fontWeight={600}
          sx={{
            minWidth: 40,
            textAlign: 'center',
            userSelect: 'none',
          }}
        >
          {canQuantidade}
        </Typography>
      )}

      <IconButton
        size={sizes.button}
        onClick={onIncrement}
        disabled={disabled || !canIncrement}
        color="primary"
      >
        <Add fontSize={size} />
      </IconButton>
    </Box>
  );
};